import { FileSchemaResponse } from "@/api/api"

const OBJECT_ID_REGEX = /^[a-z\d]{24}$/

export function isFileId(id: string): boolean {
    return OBJECT_ID_REGEX.test(id)
}

/** Path markers for workbook/email parents surfaced as navigable rows (not connector folders). */
const VIRTUAL_FOLDER_ID_MARKERS = ["xls", "ods", "csv", "xlsx", ".email"] as const

/** Connector/upload folder paths vs spreadsheet/email parents (ObjectId or *.xlsx path + is_folder). */
export function isRealFolder(item: { id: string; is_folder?: boolean }): boolean {
    if (!item.is_folder) return false
    if (isFileId(item.id)) return false
    const idLower = item.id.toLowerCase()
    if (VIRTUAL_FOLDER_ID_MARKERS.some(ext => idLower.includes(ext))) return false
    return true
}

export function resolveSelectedDocsToFileIds(
    selectedDocs: string[],
    allDocuments: FileSchemaResponse[]
): string[] {
    if (!selectedDocs?.length) return []

    const fileIds = new Set<string>()

    for (const id of selectedDocs) {
        if (isFileId(id)) {
            fileIds.add(id)
        } else {
            // Folder path — expand to all contained file IDs
            for (const doc of allDocuments ?? []) {
                if (
                    !doc.is_folder &&
                    isFileId(doc.id) &&
                    doc.file_folder &&
                    (doc.file_folder === id || doc.file_folder.startsWith(id + "/"))
                ) {
                    fileIds.add(doc.id)
                }
            }
        }
    }

    return Array.from(fileIds)
}

/**
 * Filter selectedDocs to display-worthy items: folders + standalone files
 * whose parent folder is NOT also selected.
 */
export function getDisplayItems(
    selectedDocs: string[],
    allDocuments: FileSchemaResponse[]
): string[] {
    if (!selectedDocs?.length) return []

    const selectedFolders = new Set(selectedDocs.filter((id) => !isFileId(id)))
    if (selectedFolders.size === 0) return selectedDocs

    const fileFolderMap = new Map<string, string>()
    for (const doc of allDocuments ?? []) {
        if (!doc.is_folder && isFileId(doc.id) && doc.file_folder) {
            fileFolderMap.set(doc.id, doc.file_folder)
        }
    }

    return selectedDocs.filter((id) => {
        if (!isFileId(id)) {
            for (const sf of selectedFolders) {
                if (id !== sf && id.startsWith(sf + "/")) return false
            }
            return true
        }
        const folder = fileFolderMap.get(id)
        if (!folder) return true
        for (const sf of selectedFolders) {
            if (folder === sf || folder.startsWith(sf + "/")) return false
        }
        return true
    })
}

export function getFilesInFolder(
    folderPath: string,
    allDocuments: FileSchemaResponse[]
): FileSchemaResponse[] {
    return (allDocuments ?? []).filter(
        (doc) =>
            !doc.is_folder &&
            doc.file_folder &&
            (doc.file_folder === folderPath || doc.file_folder.startsWith(folderPath + "/"))
    )
}

/**
 * Returns a Set of folder paths that have at least one selected descendant
 * (file or sub-folder) but are NOT themselves directly selected.
 * These folders should render with an indeterminate checkbox state.
 *
 * NOTE: This returns folder *paths*, not ObjectIds.
 * When displaying a folder in DataSourcesSidebar, you should check using
 * `partiallySelectedFolders.has(folderItem.id)` if `id` is a path,
 * OR `partiallySelectedFolders.has(folderItem.file_folder)` if the id is
 * an ObjectId and the path is stored in `file_folder`.
 */
export function getPartiallySelectedFolders(
    selectedDocs: string[],
    allDocuments: FileSchemaResponse[]
): Set<string> {
    if (!selectedDocs?.length) return new Set()

    const selectedSet = new Set(selectedDocs)
    const result = new Set<string>()

    for (const id of selectedDocs) {
        if (!isFileId(id)) continue // skip folder paths themselves
        // Find the file's folder path
        const doc = allDocuments?.find((d) => d.id === id)
        if (!doc?.file_folder) continue

        let folderPath = doc.file_folder
        // Walk up the folder path tree
        while (folderPath) {
            if (!selectedSet.has(folderPath)) {
                result.add(folderPath)
            }
            const lastSlash = folderPath.lastIndexOf("/")
            if (lastSlash <= 0) break
            folderPath = folderPath.slice(0, lastSlash)
        }
    }

    // Also handle selected sub-folders: if "parent/child" is selected but "parent" is not,
    // then "parent" should be indeterminate
    for (const id of selectedDocs) {
        if (isFileId(id)) continue
        let folderPath = id
        while (folderPath) {
            const lastSlash = folderPath.lastIndexOf("/")
            if (lastSlash <= 0) break
            folderPath = folderPath.slice(0, lastSlash)
            if (!selectedSet.has(folderPath)) {
                result.add(folderPath)
            }
        }
    }

    return result
}

/**
 * Returns whether a specific displayed folder item should render with an
 * indeterminate (partially selected) checkbox.
 *
 * Unlike getPartiallySelectedFolders, this function works with the actual
 * folder item as it appears in the UI, handling both ObjectId-based ids
 * and path-based ids.
 */
export function isFolderPartiallySelected(
    folderItem: FileSchemaResponse,
    selectedDocs: string[],
    allDocuments: FileSchemaResponse[]
): boolean {
    if (!selectedDocs?.length) return false
    if (!folderItem.is_folder) return false

    // If the folder itself is directly selected, it's not "partially" selected
    if (selectedDocs.includes(folderItem.id)) return false

    // Determine the path prefix for this folder.
    // Strategy: find the folder in allDocuments and use its file_folder.
    // If file_folder isn't available there, fall back to the id or file_name.
    const folderDoc = allDocuments?.find(
        (d) => d.is_folder && (d.id === folderItem.id || d.file_name === folderItem.file_name)
    )

    const possiblePaths: string[] = []
    if (folderDoc?.file_folder) possiblePaths.push(folderDoc.file_folder)
    if (folderItem.file_folder) possiblePaths.push(folderItem.file_folder)
    if (!isFileId(folderItem.id)) possiblePaths.push(folderItem.id)

    if (possiblePaths.length === 0) return false

    for (const selectedId of selectedDocs) {
        if (isFileId(selectedId)) {
            // It's a file ID — find its folder path and see if it's under this folder
            const doc = allDocuments?.find((d) => d.id === selectedId)
            if (doc?.file_folder) {
                for (const prefix of possiblePaths) {
                    if (doc.file_folder.startsWith(prefix + "/")) return true
                }
            }
        } else {
            // It's a folder path selected — check if it's a descendant
            for (const prefix of possiblePaths) {
                if (selectedId.startsWith(prefix + "/")) return true
            }
        }
    }

    return false
}
